import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: '天天漫画是免费使用的吗？',
    answer: '天天漫画的基础功能完全免费，包括免费阅读大量正版漫画、基础阅读模式、社区互动等。',
  },
  {
    question: '如何下载天天漫画APP？',
    answer: '天天漫画APP下载非常简单，你可以直接访问我们的下载页面，根据你的设备选择对应的版本下载安装。我们支持Android、iOS、HarmonyOS和Windows四大平台。天天漫画下载安全无毒，请放心使用。',
  },
  {
    question: '天天漫画的优质漫画资源多吗？',
    answer: '天天漫画目前收录超过10万部漫画，涵盖日漫、国漫、韩漫等全品类，包括热血、恋爱、悬疑、搞笑、日常、异世界等各大类型。我们与数百家出版社和作者合作，每日更新上千章节。',
  },
  {
    question: '天天漫画支持离线阅读吗？',
    answer: '是的，天天漫画支持极速离线缓存功能。你可以在Wi-Fi环境下下载整本漫画或单个章节，下载后无需网络即可随时随地阅读。缓存功能完全免费，方便你在通勤、旅行等场景使用。',
  },
  {
    question: '如何更换设备后同步阅读进度？',
    answer: '天天漫画支持多端同步功能。只要使用同一个账号登录，你的阅读进度、书签、收藏列表等数据会自动同步到所有设备。支持手机、平板、电脑等多端无缝切换。',
  },
  {
    question: '天天漫画官方正版下载渠道有哪些？',
    answer: '天天漫画官方正版下载仅在本网站下载页面和各平台官方应用商店提供。请勿从未知第三方网站下载，以确保安全。iOS用户可在App Store搜索"天天漫画"下载，Android用户可在各大应用市场下载。',
  },
  {
    question: '天天漫画APP下载后如何更新？',
    answer: '天天漫画支持自动更新功能。当有新版本发布时，应用会提示你更新。你也可以在应用商店手动检查更新。我们平均每月发布一次更新，持续优化阅读体验。',
  },
  {
    question: '遇到阅读问题如何反馈？',
    answer: '你可以通过APP内的"设置-意见反馈"提交问题，我们的客服团队会在24小时内回复。你也可以通过官方社交媒体账号联系我们，或发送邮件至客服邮箱。',
  },
];

const guides = [
  {
    icon: 'ri-smartphone-line',
    title: '下载安装指南',
    desc: '了解如何在你的设备上完成天天漫画APP下载和安装。',
  },
  {
    icon: 'ri-user-star-line',
    title: '账号注册教程',
    desc: '快速注册天天漫画账号，开启你的个性化阅读之旅。',
  },
  {
    icon: 'ri-book-open-line',
    title: '阅读设置说明',
    desc: '掌握翻页模式、亮度调节、背景色等个性化阅读设置。',
  },
  {
    icon: 'ri-cloud-line',
    title: '离线缓存教程',
    desc: '学会使用离线缓存功能，无网络也能畅快阅读漫画。',
  },
];

function ScrollReveal({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </div>
  );
}

function FAQItem({ faq, isOpen, onClick }: { faq: typeof faqs[0]; isOpen: boolean; onClick: () => void }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left"
      >
        <span className="font-bold text-gray-900 text-sm md:text-base">{faq.question}</span>
        <i className={`ri-${isOpen ? 'subtract' : 'add'}-line text-pink-500 text-xl flex-shrink-0 transition-transform duration-300`} />
      </button>
      {isOpen && (
        <div className="px-5 md:px-6 pb-5 md:pb-6">
          <p className="text-sm text-gray-500 leading-relaxed">{faq.answer}</p>
        </div>
      )}
    </div>
  );
}

export default function Help() {
  const [openIndex, setOpenIndex] = useState<number>(0);

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Header */}
      <section className="px-4 md:px-8 lg:px-16 xl:px-24 py-20 md:py-28">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-pink-50 text-pink-600 text-sm font-medium mb-4">
                帮助中心
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                有什么可以帮您
              </h1>
              <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                浏览常见问题解答或使用指南，快速解决你在使用天天漫画过程中遇到的问题。如果仍有疑问，欢迎联系我们的客服团队。
              </p>
            </div>
          </ScrollReveal>

          {/* Guides */}
          <ScrollReveal>
            <div id="guides" className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6 mb-16 scroll-mt-24">
              {guides.map((guide) => (
                <div
                  key={guide.title}
                  className="relative bg-white rounded-2xl border-2 border-gray-900 p-6 pt-9 text-center overflow-hidden cursor-pointer group hover:-translate-y-1.5 transition-transform shadow-[4px_4px_0_0_#f9a8d4]"
                >
                  <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(236,72,153,0.12)_1.5px,transparent_1.5px)] [background-size:10px_10px] pointer-events-none" />
                  <div className="absolute top-2.5 right-2.5 w-2.5 h-2.5 rounded-full bg-amber-300 border border-gray-900" />

                  <div className="relative flex flex-col items-center">
                    <div className="relative mb-7">
                      <div className="w-16 h-14 bg-white border-2 border-gray-900 rounded-xl flex items-center justify-center relative z-10 group-hover:rotate-3 transition-transform">
                        <i className={`${guide.icon} text-2xl text-pink-500`} />
                      </div>
                      <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-b-2 border-r-2 border-gray-900 rotate-45" />
                    </div>

                    <h3 className="font-bold text-gray-900 text-sm mb-2">{guide.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{guide.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollReveal>

          {/* FAQ */}
          <ScrollReveal>
            <div id="faq" className="mb-4 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">常见问题</h2>
              <div className="space-y-4 max-w-3xl mx-auto">
                {faqs.map((faq, index) => (
                  <FAQItem
                    key={index}
                    faq={faq}
                    isOpen={openIndex === index}
                    onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                  />
                ))}
              </div>
            </div>
          </ScrollReveal>

          {/* Contact */}
          <ScrollReveal>
            <div className="mt-16 bg-white rounded-2xl border border-gray-100 p-8 md:p-12 shadow-sm text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">仍有问题？</h2>
              <p className="text-gray-500 mb-8 max-w-xl mx-auto">
                如果以上解答未能解决你的问题，欢迎联系我们的客服团队。我们承诺在24小时内回复你的咨询。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/" className="btn-primary inline-flex items-center gap-2">
                  <i className="ri-mail-line" /> 发送邮件
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}